import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Download, Sparkles, TrendingUp, Lightbulb, Loader2, FileWarning, Mail, X } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../components/ui/sonner';
import ScoreRing from '../components/ui-kit/ScoreRing';

/**
 * Report Screen — /report/:sessionId
 * Shows the final score, topic breakdown, and Gemini-generated insights.
 */
export default function ReportScreen() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWeak, setDownloadingWeak] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Try to fetch — if not yet completed, finalize via /complete
        let data;
        try {
          const res = await api.get(`/sessions/${sessionId}/report`, { signal: controller.signal });
          data = res.data;
          if (data.status !== 'COMPLETED' || !data.pdf_report_url) {
            await api.post(`/sessions/${sessionId}/complete`, null, { signal: controller.signal });
            const refetched = await api.get(`/sessions/${sessionId}/report`, { signal: controller.signal });
            data = refetched.data;
          }
        } catch (e) {
          if (controller.signal.aborted) return;
          // Fallback: try /complete (covers any not-yet-completed case)
          await api.post(`/sessions/${sessionId}/complete`, null, { signal: controller.signal });
          const refetched = await api.get(`/sessions/${sessionId}/report`, { signal: controller.signal });
          data = refetched.data;
        }
        if (controller.signal.aborted) return;
        setReport(data);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e?.response?.data?.detail || 'Failed to load report');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => { controller.abort(); };
  }, [sessionId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `BotView-Report-${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // ignore — UI shows error elsewhere
    } finally {
      setDownloading(false);
    }
  };

  // Generated on demand server-side, never stored — see /weak-areas-pdf.
  const handleDownloadWeakAreas = async () => {
    setDownloadingWeak(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/weak-areas-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `BotView-FocusAreas-${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not generate the focus areas PDF — please try again.');
    } finally {
      setDownloadingWeak(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="report-loading" className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#3D1F6E]" />
        <p className="text-sm text-muted-foreground">Generating your interview report...</p>
        <p className="text-xs text-muted-foreground">Scoring evaluations and crafting AI insights</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div data-testid="report-error" className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-sm text-red-500">{error || 'Report not found'}</p>
        <button
          onClick={() => navigate('/')}
          className="text-sm text-[#FF6B1A] hover:underline font-heading font-semibold"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const label = report.mode === 'company' ? report.company : report.topic;
  const topicEntries = Object.entries(report.topic_scores || {});
  const final = report.final_report || {};
  const strengths = final.strengths || [];
  const improvements = final.improvements || [];
  const recommendations = final.recommendations || [];

  const dateStr = report.ended_at
    ? new Date(report.ended_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div data-testid="report-page" className="space-y-6 max-w-5xl mx-auto stagger">
      {/* ── Header / Hero ── */}
      <div
        data-testid="report-hero"
        className="relative overflow-hidden rounded-2xl p-6 md:p-10 anim-fade-up"
        style={{ background: 'linear-gradient(135deg, #3D1F6E 0%, #1E0F3D 50%, #0C0D10 100%)' }}
      >
        <div className="absolute inset-0 circuit-pattern opacity-50 pointer-events-none" />
        <div
          className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,194,51,0.18) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-heading font-semibold mb-2">
              Interview Complete
            </p>
            <h1
              data-testid="report-title"
              className="text-3xl md:text-4xl font-bold tracking-tight font-heading text-white"
            >
              Your BotView Report
            </h1>
            <p className="text-sm text-white/70 mt-2 max-w-md">
              <span className="text-white font-semibold">{report.student_name}</span>
              {report.college_name ? ` • ${report.college_name}` : ''}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <Badge variant="secondary" className="bg-white/10 text-white border-white/10 font-heading">
                {report.mode === 'company' ? 'Company' : 'Topic'}
              </Badge>
              {label && (
                <Badge variant="secondary" className="bg-white/10 text-white border-white/10 font-heading">
                  {label}
                </Badge>
              )}
              {report.difficulty && (
                <Badge variant="secondary" className="bg-white/10 text-white border-white/10 font-heading">
                  {report.difficulty}
                </Badge>
              )}
              {dateStr && (
                <span className="text-xs text-white/60">{dateStr}</span>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="download-pdf-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold font-heading btn-cta disabled:opacity-60"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download PDF Report
              </button>
              <button
                onClick={handleDownloadWeakAreas}
                disabled={downloadingWeak}
                data-testid="download-weak-areas-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold font-heading bg-white/10 text-white hover:bg-white/20 transition-colors border border-white/15 disabled:opacity-60"
              >
                {downloadingWeak ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileWarning className="w-4 h-4" />
                )}
                Download Focus Areas
              </button>
              <button
                onClick={() => setShowEmailModal(true)}
                data-testid="email-report-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold font-heading bg-white/10 text-white hover:bg-white/20 transition-colors border border-white/15"
              >
                <Mail className="w-4 h-4" />
                Email Focus Areas
              </button>
              <button
                onClick={() => navigate('/')}
                data-testid="back-to-dashboard-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold font-heading bg-white/10 text-white hover:bg-white/20 transition-colors border border-white/15"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="flex justify-center md:justify-end">
            <ScoreRing value={report.total_score || 0} size={220} strokeWidth={16} testId="report-score-ring" />
          </div>
        </div>
      </div>

      {/* ── Topic Score Breakdown ── */}
      <Card data-testid="topic-breakdown-card" className="bg-card border-border card-shadow blob-violet anim-fade-up">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
            Topic Score Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-2">
          {topicEntries.length === 0 ? (
            <p data-testid="topic-empty" className="text-xs text-muted-foreground">
              No topic data was captured for this session.
            </p>
          ) : (
            topicEntries.map(([topic, score], i) => (
              <TopicBar key={topic} topic={topic} score={Number(score) || 0} index={i} />
            ))
          )}
        </CardContent>
      </Card>

      {/* ── Three insight cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InsightCard
          testId="strengths-card"
          icon={Sparkles}
          title="Strengths"
          items={strengths}
          accent="gold"
        />
        <InsightCard
          testId="improvements-card"
          icon={TrendingUp}
          title="Areas to Improve"
          items={improvements}
          accent="orange"
        />
        <InsightCard
          testId="recommendations-card"
          icon={Lightbulb}
          title="Recommendations"
          items={recommendations}
          accent="violet"
        />
      </div>

      {/* ── Summary ── */}
      {final.summary && (
        <Card data-testid="summary-card" className="bg-card border-border card-shadow anim-fade-up">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              Overall Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p data-testid="summary-text" className="text-sm leading-relaxed text-foreground/90">
              {final.summary}
            </p>
          </CardContent>
        </Card>
      )}

      {showEmailModal && (
        <EmailFocusAreasModal sessionId={sessionId} onClose={() => setShowEmailModal(false)} />
      )}
    </div>
  );
}

function EmailFocusAreasModal({ sessionId, onClose }) {
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const list = emails.split(',').map((e) => e.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error('Enter at least one email address.');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/weak-areas-pdf/email`, { emails: list });
      toast.success(data.message || 'Focus areas PDF sent.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full space-y-4" data-testid="email-focus-areas-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-heading font-semibold">Email Focus Areas</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-email-modal-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Sends the same focus-areas PDF as an attachment. Generated fresh for this email and not stored anywhere.
        </p>
        <input
          type="text"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="you@example.com, mentor@example.com"
          data-testid="email-focus-areas-input"
          className="w-full px-3 py-2.5 rounded-lg bg-secondary/40 border border-border text-sm focus:outline-none focus:border-[#3D1F6E] dark:focus:border-[#A78BFA] transition-colors"
        />
        <p className="text-[11px] text-muted-foreground">Separate multiple addresses with commas.</p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-secondary text-foreground hover:bg-secondary/70 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            data-testid="send-email-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold font-heading btn-cta disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function TopicBar({ topic, score, index }) {
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div data-testid={`topic-bar-${index}`} className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium">{topic}</span>
        <span className="text-[13px] font-heading font-bold tabular-nums">{pct.toFixed(0)}</span>
      </div>
      <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${Math.max(pct, 2)}%`,
            background: 'linear-gradient(90deg, #3D1F6E 0%, #5B2D9E 100%)',
          }}
        />
      </div>
    </div>
  );
}

const ACCENT = {
  gold: { ring: 'icon-bg-gold', dot: '#FFC233' },
  orange: { ring: 'icon-bg-orange', dot: '#FF6B1A' },
  violet: { ring: 'icon-bg-violet', dot: '#3D1F6E' },
};

function InsightCard({ testId, icon: Icon, title, items, accent = 'violet' }) {
  const acc = ACCENT[accent] || ACCENT.violet;
  return (
    <Card data-testid={testId} className={`bg-card border-border card-shadow blob-${accent} anim-fade-up`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${acc.ring}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
            {title}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground">No insights available.</p>
        ) : (
          <ul className="space-y-2.5">
            {items.map((it, i) => (
              <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed">
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: acc.dot }}
                />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
